import axios from 'axios';
import { toast } from 'react-toastify';
import { base_url } from '../config';
import { getErrorMessage, isTimeoutError } from '../utils/errors';
import { expireSession } from '../utils/session';

const api = axios.create({
  baseURL: base_url,
  timeout: 30000,
});

// Request interceptor — attach the bearer token.
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(normalizeError(error))
);

// Response interceptor — global handling for conditions no single page owns
// (connectivity, session expiry, permissions, rate limits, server outages).
// Validation errors (400/404/409/422) are left to the calling page, which knows
// which form field or record they refer to.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (axios.isCancel(error)) return Promise.reject(error);

    const status = error.response?.status;

    if (!error.response) {
      notify(isTimeoutError(error) ? 'timeout' : 'network', getErrorMessage({ status: 0, code: error.code, isAxiosError: true }));
    } else if (status === 401) {
      handleUnauthorized();
    } else if (status === 403) {
      notify('forbidden', getErrorMessage({ status }));
    } else if (status === 429) {
      notify('rate-limit', getErrorMessage({ status }));
    } else if (status >= 500) {
      notify('server', getErrorMessage({ status }));
    }

    if (process.env.NODE_ENV !== 'production') {
      console.error(`[api] ${error.config?.method?.toUpperCase()} ${error.config?.url} → ${status ?? error.code}`, error.response?.data);
    }

    return Promise.reject(normalizeError(error));
  }
);

/** Consistent error shape for callers: { message, status, code, data, isAxiosError }. */
function normalizeError(error) {
  return {
    message: getErrorMessage(error),
    status: error?.response?.status || 0,
    code: error?.code,
    data: error?.response?.data,
    isAxiosError: Boolean(error?.isAxiosError),
  };
}

// One toast per category at a time, so parallel failures don't stack up.
function notify(id, message) {
  if (toast.isActive(id)) return;
  toast.error(message, { toastId: id });
}

let redirectingToLogin = false;
function handleUnauthorized() {
  if (redirectingToLogin) return;
  redirectingToLogin = true;
  expireSession();
  if (!window.location.pathname.toLowerCase().startsWith('/login')) {
    window.location.assign('/login?session=expired');
  } else {
    redirectingToLogin = false;
  }
}

export default api;
